
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import type { AssistantFormData } from "./types";
import { getOpenAIClient } from "@/lib/openai";
import type { AssistantCreateParams } from "openai/resources/beta/assistants";

export const useAssistantCreation = (
  onSave: (assistant: { id: string; name: string }) => void,
  onClose?: () => void
) => {
  const [isLoading, setIsLoading] = useState(false);

  const createAssistant = async (formData: AssistantFormData) => {
    console.log('Creating assistant with data:', formData);
    setIsLoading(true);

    try {
      const openai = await getOpenAIClient();

      // Prepare assistant parameters
      const assistantParams: AssistantCreateParams = {
        name: formData.name,
        instructions: formData.instructions,
        model: formData.model,
      };

      // Check if we have a vector store to enable file search
      if (formData.vectorStoreId) {
        try {
          // Get the OpenAI vector store ID
          const { data: vectorStore } = await supabase
            .from('vector_stores')
            .select('openai_vector_store_id')
            .eq('id', formData.vectorStoreId)
            .single();

          if (vectorStore?.openai_vector_store_id) {
            // Add file search tool and vector store configuration
            assistantParams.tools = [{ type: "file_search" }];
            assistantParams.tool_resources = {
              file_search: {
                vector_store_ids: [vectorStore.openai_vector_store_id]
              }
            };
            console.log('Enabling file search with vector store:', vectorStore.openai_vector_store_id);
          }
        } catch (error) {
          console.error('Error setting up vector store for assistant:', error);
          // Continue without vector store if there's an error
        }
      }

      // Create the assistant with all configurations
      console.log('Creating OpenAI assistant with params:', assistantParams);
      const assistant = await openai.beta.assistants.create(assistantParams);
      console.log('Assistant created in OpenAI:', assistant);

      // Save assistant to database
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('No authenticated user found');

      const { data: assistantData, error: dbError } = await supabase
        .from('assistants')
        .insert({
          assistant_id: assistant.id,
          name: formData.name,
          description: formData.instructions,
          user_id: session.user.id,
          model: formData.model,
          creativity: formData.creativity,
          vector_store_id: formData.vectorStoreId || null
        })
        .select()
        .single();

      if (dbError) {
        if (dbError.code === 'P0001' && dbError.message.includes('Assistant limit')) {
          throw new Error('You have reached the maximum number of assistants allowed in your plan. Please upgrade your plan or delete an existing assistant.');
        }
        throw dbError;
      }

      // Create file associations in our database if we have a vector store
      if (formData.vectorStoreId) {
        try {
          const { data: vectorStoreFiles } = await supabase
            .from('vector_store_files')
            .select('id, openai_file_id')
            .eq('vector_store_id', formData.vectorStoreId);

          if (vectorStoreFiles && vectorStoreFiles.length > 0) {
            // Create associations in assistant_files table
            const { error: filesError } = await supabase
              .from('assistant_files')
              .insert(
                vectorStoreFiles.map(file => ({
                  assistant_id: assistantData.id,
                  file_id: file.id,
                  openai_file_id: file.openai_file_id
                }))
              );

            if (filesError) throw filesError;
          }
        } catch (error) {
          console.error('Error creating file associations:', error);
          // We continue anyway since the assistant was created successfully
        }
      }

      console.log('Assistant saved to database:', assistantData);
      toast.success("Assistant created successfully");
      onSave(assistantData);
      
      // Close the dialog if onClose is provided
      if (onClose) {
        onClose();
      }
      
    } catch (error) {
      console.error('Error creating assistant:', error);
      // Let the error propagate to the parent component
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createAssistant
  };
};
